import { TranslateModule } from '@ngx-translate/core';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { CurrentUserService } from '../../shared/services/current-user.service';
import { VersionService } from '../../shared/services/version.service';
import {
  BaseComponent,
  ErrorMessageComponent,
  LanguageStore,
  LoadingService,
  locales,
  StorageKeyConstants,
} from '@tfagency/shared/util';
import { RouteConstants } from '../../shared/constants/route.constants';
import { RoleType } from '@tfagency/core/data-access';
import { filter, tap } from 'rxjs';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { toSignal } from '@angular/core/rxjs-interop';
import { PortalStorageKeyConstants } from '../../shared/constants/portal-storage-key.constants';
import { BreadcrumbStore } from '../../shared/components/breadcrumb/breadcrumb.store';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'tfagency-shell',
  imports: [
    CommonModule,
    TranslateModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatListModule,
    MatProgressBarModule,
    RouterModule,
    ErrorMessageComponent,
    NgOptimizedImage,
    MatTooltipModule,
  ],
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent extends BaseComponent implements OnInit, OnDestroy {
  readonly store = inject(BreadcrumbStore);
  readonly languageStore = inject(LanguageStore);
  readonly versionService = inject(VersionService);

  readonly RoleType = RoleType;

  mainRoutes = {
    dashboardRoute: `/${RouteConstants.DASHBOARD_ROUTE}`,
    propertiesRoute: `/${RouteConstants.STB_PROPERTIES_ROUTE}`,
    marketAnalysisRoute: `/${RouteConstants.MARKET_ANALYSIS_ROUTE}`,
    assignPropertiesRoute: `/${RouteConstants.ASSIGN_PROPERTIES_ROUTE}`,
    leadsRoute: `/${RouteConstants.LEADS_ROUTE}`,
    newsFeedsRoute: `/${RouteConstants.NEWS_FEED_ROUTE}`,
    brokersStaffRoute: `/${RouteConstants.BROKERS_STAFF_ROUTE}`,
  };
  localesList = locales.localesList;
  isCollapsed = false;

  routerEvents = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      tap(() => {
        this.errorMessage = null;
      }),
    ),
  );

  constructor(
    private router: Router,
    private oauthService: OAuthService,
    private currentUserService: CurrentUserService,
    public loadingService: LoadingService,
  ) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.isCollapsed = localStorage.getItem(PortalStorageKeyConstants.IS_MENU_COLLAPSED) === 'true';
    this.fullName = this.currentUserService.getFullName();
    this.role = this.currentUserService.getRole();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
  }

  toggleSidenav() {
    this.isCollapsed = !this.isCollapsed;
    localStorage.setItem(PortalStorageKeyConstants.IS_MENU_COLLAPSED, this.isCollapsed.toString());
  }

  openGroupChat() {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/${RouteConstants.GROUP_CHAT_ROUTE}`]));
    window.open(url, '_blank');
  }

  async logoff() {
    await this.oauthService.revokeTokenAndLogout();
    localStorage.removeItem(PortalStorageKeyConstants.STB_PROPERTIES_QUERY);
    localStorage.removeItem(PortalStorageKeyConstants.IS_MENU_COLLAPSED);
  }

  reload() {
    location.reload();
  }

  navigateToPage(routePath: string) {
    this.router.navigate([`${routePath}`]);
    this.errorMessage = null;
  }
}
